import { Modal, Input, Select, Button } from 'antd'
import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CloseOutlined } from '@ant-design/icons'
import { fetchItemsGroups, fetchUoMGroups } from '@utils/fetchData'
import { useQuery } from '@tanstack/react-query'
import { http } from '@services/http'
import SuccessModal from './SuccessModal'
import ErrorModal from './ErrorModal'
import { useSelector } from 'react-redux'
import { formatNumberWithSpaces, removeSpaces } from '@utils/numberWithSpaces'

const EditItemsModal = ({
	visible,
	data,
	onClose,
	onClick,
	finish = false,
}) => {
	const { t } = useTranslation()
	const { roleNames } = useSelector((state) => state.main)

	const successRef = useRef()
	const errorRef = useRef()

	const isAdmin = roleNames.includes('admin')

	const [loading, setLoading] = useState('')
	const [itemName, setItemName] = useState('')
	const [width, setWidth] = useState('')
	const [height, setHeight] = useState('')
	const [quantity, setQuantity] = useState('')
	const [price, setPrice] = useState('')
	const [currency, setCurrency] = useState('')
	const [itmsGrp, setItmsGrp] = useState('')
	const [ugpName, setUgpName] = useState('')
	const [timer, setTimer] = useState('')
	const [koef, setKoef] = useState(null)
	const [u_Number, setU_Number] = useState('')
	const [kg, setKg] = useState('')
	useEffect(() => {
		if (data) {
			setItemName(data.itemName)
			setWidth(data.u_Width ?? '')
			setHeight(data.u_Height ?? '')
			setQuantity(data.u_Kvmetr ?? '')
			setPrice(data.price ?? '')
			setCurrency(data.currency ?? '')
			setItmsGrp(data.itmsGrpCod ?? '')
			setUgpName(data.ugpEntry ?? '')
			setTimer(data.u_Timer ?? '')
			setKoef(data.u_DefaultCoefficient ?? 1)
			setU_Number(data.u_Number ?? '')
			setKg(data.u_Kg ?? '')
		}
	}, [data, visible])

	const {
		data: uomData = [],
		isLoading,
		refetch,
	} = useQuery({
		queryKey: ['uoMEntry'],
		queryFn: fetchUoMGroups,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const { data: itemsGroupData = [], isLoading: itemsLoading } = useQuery({
		queryKey: ['itemsGrp'],
		queryFn: fetchItemsGroups,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const postData = async () => {
		setLoading(true)

		const widthWithoutSpaces = removeSpaces(width)
		const heightWithoutSpaces = removeSpaces(height)
		const priceWithoutSpaces = removeSpaces(price)

		try {
			await http.patch(`api/items/${data.itemCode}`, {
				itemName,
				itemsGroupCode: itmsGrp,
				price: priceWithoutSpaces || 0,
				currency,
				u_Width: widthWithoutSpaces,
				u_Height: heightWithoutSpaces,
				u_Kvmetr: (heightWithoutSpaces * widthWithoutSpaces) / 1000000,
				uoMGroupEntry: ugpName,
				u_Timer: '',
				u_DefaultCoefficient: koef,
				u_Number: u_Number,
				u_Kg: kg,
				u_Modal: data?.u_Modal,
			})
			successRef.current.open(t('sucEdited'))
			resetForm()
		} catch (error) {
			errorRef.current.open(error?.response?.data?.message)
		} finally {
			setLoading(false)
		}
	}

	const resetForm = () => {
		setLoading(false)
		setItemName('')
		setWidth('')
		setHeight('')
		setQuantity('')
		setPrice('')
		setCurrency('')
		setItmsGrp('')
		setUgpName('')
		setTimer('')
		setKoef('1')
		setU_Number('')
	}

	if (!data) return null

	return (
		<Modal
			title={
				<h1 className="font-nunito text-xl font-extrabold text-[#000000]">
					{t('infoItem')}
				</h1>
			}
			open={visible}
			onCancel={onClose}
			footer={null}
			closeIcon={<CloseOutlined />}
			width="75%"
			style={{ height: '80%' }}
			bodyStyle={{ height: 'calc(100% - 55px)', overflowY: 'auto' }}
		>
			<div className="w-full px-4 sm:px-5">
				<div className="w-full">
					<div className="mt-10 flex flex-col items-center gap-7 justify-between sm:flex-row">
						<div className="flex flex-col gap-3 w-full">
							<p className="font-nunito">{t('itemName')}</p>
							<Input
								type="text"
								value={itemName}
								className="flex h-10  w-full min-w-[240px] items-center justify-center "
								style={{ color: 'black' }}
								onChange={(e) => {
									setItemName(e.target.value)
								}}
							/>
						</div>

						<div className="mt-4 flex flex-col gap-3 sm:mt-0">
							<p className="font-nunito">{t('productGroup')}</p>
							<Select
								value={itmsGrp}
								className="flex h-10  w-[200px] items-center justify-center "
								style={{ color: 'black' }}
								onChange={(val) => {
									setItmsGrp(val)
								}}
								options={itemsGroupData
									.filter(
										(item) =>
											item.itmsGrpCod === '100' ||
											item.itmsGrpCod === '103' ||
											item.itmsGrpCod === '104' ||
											item.itmsGrpCod === '105',
									)
									.map((item) => {
										return {
											label: item.itmsGrpNam,
											value: Number(item.itmsGrpCod),
										}
									})}
							/>
						</div>

						<div className="mt-4 flex flex-col gap-3 sm:mt-0">
							<p className="font-nunito">{t('currency')}</p>
							<Select
								value={currency}
								className="flex h-10  w-[200px] items-center justify-center "
								style={{ color: 'black' }}
								onChange={(val) => {
									setCurrency(val)
								}}
								options={[
									{
										label: 'UZS',
										value: 'UZS',
									},
									{
										label: 'USD',
										value: 'USD',
									},
								]}
							/>
						</div>
						<div className="flex flex-col gap-3 w-full">
							<p className="font-nunito">{t('Koefitsent')}</p>
							<Input
								type="number"
								value={koef}
								className="flex h-10  w-full items-center justify-center "
								style={{ color: 'black' }}
								onChange={(e) => {
									setKoef(e.target.value)
								}}
								onWheel={(e) => e.target.blur()}
								disabled={!isAdmin}
							/>
						</div>
					</div>

					<div className="mt-10 flex flex-col items-center gap-7  justify-between sm:flex-row">
						<div className=" flex flex-col gap-3  min-w-[150px] w-full">
							<p>{`${t('unitMsr')}`}</p>
							<Select
								type="text"
								value={ugpName}
								onChange={(val) => {
									setUgpName(val)
								}}
								className="flex h-10 w-full  items-center justify-center border-[#D0D0D0]"
								options={uomData.map((item) => ({
									label: item.ugpName,
									value: item.ugpEntry,
								}))}
							/>
						</div>
						<div className="flex flex-col gap-3">
							<p className="font-nunito">{`${t('price')} `}</p>
							<Input
								type="text"
								value={formatNumberWithSpaces(price)}
								className="flex h-10 w-auto items-center justify-center"
								style={{ color: 'black' }}
								onChange={(e) => {
									setPrice(e.target.value)
								}}
								// disabled={isAdmin}
							/>
						</div>
						<div className="flex flex-col gap-3">
							<p className="font-nunito">{`${t('width')} (${t('mm')})`}</p>
							<Input
								type="text"
								value={formatNumberWithSpaces(width)}
								className="flex h-10  w-auto items-center justify-center "
								style={{ color: 'black' }}
								onChange={(e) => {
									setWidth(e.target.value)
								}}
							/>
						</div>

						<div className="mt-4 flex flex-col gap-3 sm:mt-0">
							<p className="font-nunito">{`${t('height')} (${t('mm')})`}</p>
							<Input
								value={formatNumberWithSpaces(height)}
								type="text"
								className="flex h-10  w-auto items-center justify-center "
								style={{ color: 'black' }}
								onChange={(e) => {
									setHeight(e.target.value)
								}}
							/>
						</div>
						<div className="mt-4 flex flex-col gap-3 sm:mt-0">
							<p>{`${t('kg')}`}</p>
							<Input
								value={formatNumberWithSpaces(kg)}
								type="text"
								onChange={(e) => {
									setKg(e.target.value)
								}}
								className="flex h-10  w-[100px] items-center justify-center "
							/>
						</div>
					</div>

					<div className="flex items-center gap-10 mt-14 justify-end">
						<Button
							onClick={postData}
							loading={loading}
							disabled={loading}
							className="w-auto bg-[#0A4D68] h-[35px] rounded-lg text-white"
						>
							{t('save')}
						</Button>
					</div>
				</div>
			</div>
			<SuccessModal
				getRef={(r) => {
					successRef.current = r
				}}
				onConfirm={() => onClose()}
			/>
			<ErrorModal
				getRef={(r) => {
					errorRef.current = r
				}}
			/>
		</Modal>
	)
}

export default EditItemsModal
